/**
 * CareTeamModal Component
 *
 * Modal for managing care team members with v2.3 ACL dual-role system.
 * Supports invitation workflow for users who don't have accounts yet.
 *
 * - Relationship: WHO the care team member is (self, parent, child, spouse, etc.)
 * - Role: WHAT the care team member can do (admin, editor, viewer, contact)
 *
 * Based on CASEFILE_FUNCTIONAL_SPEC.md Section 8 and v2.3 ACL schema.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  X,
  UserPlus,
  Trash2,
  Shield,
  AlertCircle,
  Check,
  Loader2,
  Mail,
  Clock,
  Copy,
  RefreshCw,
} from 'lucide-react';
import { Modal, ModalCloseButton } from '../ui/Modal';

// v2.3 Relationship types - WHO the care team member is
type Relationship =
  | 'self'
  | 'parent'
  | 'child'
  | 'spouse'
  | 'sibling'
  | 'grandparent'
  | 'extended'
  | 'other';

// v2.3 Role types - WHAT the care team member can do
type Role = 'admin' | 'editor' | 'viewer' | 'contact';

// Flexible interface to accept both v2.3 and legacy data structures
export interface CareTeamMemberData {
  userId: string;
  name?: string;
  email?: string;
  relationship: string;
  role?: string;
  permissions?: string;
  addedAt?: { _seconds: number };
}

// Invitation data structure
interface InvitationData {
  code: string;
  inviteeEmail: string;
  inviteeName?: string;
  relationship: string;
  role: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  createdAt: string;
  expiresAt: string;
}

interface CareTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseFileId: string;
  careRecipientName: string;
  careTeam: CareTeamMemberData[];
  ownerId: string;
  isLocked?: boolean;
  lockReason?: string;
  onCareTeamUpdated: () => void;
}

// Relationship labels for display
const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  self: 'Self (Care Recipient)',
  parent: 'Parent',
  child: 'Adult Child',
  spouse: 'Spouse/Partner',
  sibling: 'Sibling',
  grandparent: 'Grandparent',
  extended: 'Extended Family',
  other: 'Other (Friend, Neighbor, etc.)',
};

// Legacy relationship labels (for backward compatibility)
const LEGACY_RELATIONSHIP_LABELS: Record<string, string> = {
  primary: 'Primary Caregiver',
  secondary: 'Secondary Caregiver',
  medical: 'Medical Provider',
  educational: 'School Staff',
  coordinator: 'Care Coordinator',
  professional: 'Professional Caretaker',
};

// Role labels and descriptions
const ROLE_INFO: Record<Role, { label: string; description: string }> = {
  admin: {
    label: 'Admin',
    description: 'Full edit access + manage team (cannot delete CaseFile)',
  },
  editor: {
    label: 'Editor',
    description: 'Can edit documents and care logs',
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to all information',
  },
  contact: {
    label: 'Contact Only',
    description: 'No system access - contact info stored for reference',
  },
};

function getRoleBadgeClass(role: string | undefined): string {
  switch (role) {
    case 'admin':
    case 'owner':
      return 'badge badge-success';
    case 'editor':
      return 'badge badge-primary';
    case 'viewer':
    case 'logger':
      return 'badge badge-secondary';
    case 'contact':
      return 'badge';
    default:
      return 'badge';
  }
}

function formatRelationship(rel: string): string {
  return (
    RELATIONSHIP_LABELS[rel as Relationship] ||
    LEGACY_RELATIONSHIP_LABELS[rel] ||
    rel
  );
}

function formatRole(member: CareTeamMemberData | InvitationData): string {
  const role = member.role;
  if (role) {
    const roleInfo = ROLE_INFO[role as Role];
    return roleInfo?.label || role;
  }
  if ('permissions' in member && member.permissions) {
    return member.permissions.charAt(0).toUpperCase() + member.permissions.slice(1);
  }
  return 'Unknown';
}

function getStatusBadge(status: string): { class: string; label: string } {
  switch (status) {
    case 'pending':
      return { class: 'badge bg-amber-100 text-amber-800', label: 'Pending' };
    case 'accepted':
      return { class: 'badge badge-success', label: 'Accepted' };
    case 'revoked':
      return { class: 'badge bg-red-100 text-red-800', label: 'Revoked' };
    case 'expired':
      return { class: 'badge bg-gray-100 text-gray-600', label: 'Expired' };
    default:
      return { class: 'badge', label: status };
  }
}

export function CareTeamModal({
  isOpen,
  onClose,
  caseFileId,
  careRecipientName,
  careTeam,
  ownerId,
  isLocked = false,
  lockReason,
  onCareTeamUpdated,
}: CareTeamModalProps): JSX.Element | null {
  // Tab state
  const [activeTab, setActiveTab] = useState<'members' | 'invitations'>('members');

  // Add member form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRelationship, setNewMemberRelationship] = useState<Relationship>('other');
  const [newMemberRole, setNewMemberRole] = useState<Role>('viewer');

  // Invitation state
  const [invitations, setInvitations] = useState<InvitationData[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  // Operation state
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load invitations when modal opens or tab changes to invitations
  const loadInvitations = useCallback(async () => {
    if (!caseFileId) return;

    setLoadingInvitations(true);
    try {
      const listInvitations = httpsCallable<
        { caseFileId: string },
        { success: boolean; invitations: InvitationData[] }
      >(functions, 'listCareTeamInvitations');

      const result = await listInvitations({ caseFileId });
      if (result.data.success) {
        setInvitations(result.data.invitations);
      }
    } catch (err) {
      console.error('Error loading invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  }, [caseFileId]);

  // Load invitations when switching to invitations tab
  useEffect(() => {
    if (isOpen && activeTab === 'invitations') {
      loadInvitations();
    }
  }, [isOpen, activeTab, loadInvitations]);

  // Clear state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccessMessage(null);
      setShowAddForm(false);
      setGeneratedCode(null);
      setCodeCopied(false);
      resetAddForm();
    }
  }, [isOpen]);

  const resetAddForm = () => {
    setNewMemberEmail('');
    setNewMemberName('');
    setNewMemberRelationship('other');
    setNewMemberRole('viewer');
  };

  const handleAddMember = async () => {
    if (!newMemberEmail.trim()) {
      setError('Email address is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsAdding(true);
    setError(null);
    setSuccessMessage(null);
    setGeneratedCode(null);

    try {
      // Always create an invitation - let the invitee accept it
      const createInvitation = httpsCallable<
        {
          caseFileId: string;
          inviteeEmail: string;
          inviteeName?: string;
          relationship: string;
          role: string;
        },
        {
          success: boolean;
          invitationCode: string;
          expiresAt: string;
          message: string;
        }
      >(functions, 'createCareTeamInvitation');

      const result = await createInvitation({
        caseFileId,
        inviteeEmail: newMemberEmail.trim().toLowerCase(),
        inviteeName: newMemberName.trim() || undefined,
        relationship: newMemberRelationship,
        role: newMemberRole,
      });

      if (result.data.success) {
        setGeneratedCode(result.data.invitationCode);
        setSuccessMessage(
          `Invitation created! Share the code below with ${newMemberName || newMemberEmail}`
        );
        // Refresh invitations list
        loadInvitations();
      } else {
        setError(result.data.message || 'Failed to create invitation');
      }
    } catch (err: any) {
      console.error('Error creating invitation:', err);
      const errorMessage =
        err?.message || err?.details?.message || 'Failed to create invitation';
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  const handleCopyCode = async () => {
    if (!generatedCode) return;

    try {
      await navigator.clipboard.writeText(generatedCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = generatedCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleRemoveMember = async (userId: string, memberName?: string) => {
    setIsRemoving(userId);
    setError(null);
    setSuccessMessage(null);

    try {
      const manageCareTeam = httpsCallable<
        { caseFileId: string; operation: 'add' | 'remove'; userId: string },
        { success: boolean; message: string }
      >(functions, 'manageCareTeam');

      const result = await manageCareTeam({
        caseFileId,
        operation: 'remove',
        userId,
      });

      if (result.data.success) {
        setSuccessMessage(`Successfully removed ${memberName || userId} from the care team`);
        onCareTeamUpdated();
      } else {
        setError(result.data.message || 'Failed to remove care team member');
      }
    } catch (err: any) {
      console.error('Error removing care team member:', err);
      setError(err?.message || 'Failed to remove care team member');
    } finally {
      setIsRemoving(null);
    }
  };

  const handleRevokeInvitation = async (code: string) => {
    setIsRevoking(code);
    setError(null);
    setSuccessMessage(null);

    try {
      const revokeInvitation = httpsCallable<
        { caseFileId: string; invitationCode: string },
        { success: boolean; message: string }
      >(functions, 'revokeCareTeamInvitation');

      const result = await revokeInvitation({ caseFileId, invitationCode: code });

      if (result.data.success) {
        setSuccessMessage('Invitation revoked');
        loadInvitations();
      } else {
        setError(result.data.message || 'Failed to revoke invitation');
      }
    } catch (err: any) {
      console.error('Error revoking invitation:', err);
      setError(err?.message || 'Failed to revoke invitation');
    } finally {
      setIsRevoking(null);
    }
  };

  const pendingInvitations = invitations.filter((i) => i.status === 'pending');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Care Team"
      size="2xl"
      headerSlot={
        <p className="text-sm text-muted mt-1">
          Manage who can view and edit {careRecipientName}'s CaseFile
        </p>
      }
    >
      {/* Tabs */}
      <div className="flex gap-4 border-b border-soft-neutral mb-4">
              <button
                onClick={() => setActiveTab('members')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'members'
                    ? 'border-evergreen text-evergreen'
                    : 'border-transparent text-muted hover:text-ink'
                }`}
              >
                Members ({careTeam.length})
              </button>
              <button
                onClick={() => setActiveTab('invitations')}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'invitations'
                    ? 'border-evergreen text-evergreen'
                    : 'border-transparent text-muted hover:text-ink'
                }`}
              >
                Pending Invitations
                {pendingInvitations.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                    {pendingInvitations.length}
                  </span>
                )}
              </button>
      </div>

      {/* Content */}
      <div>
            {/* Locked state warning */}
            {isLocked && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">CaseFile is Locked</p>
                  <p className="text-xs text-amber-700 mt-1">
                    {lockReason === 'pending_patient_authorization'
                      ? 'Complete the patient authorization process to modify the care team.'
                      : lockReason === 'pending_adult_transition'
                      ? 'The care recipient has turned 18. Complete the adult transition process.'
                      : lockReason === 'authorization_revoked'
                      ? 'Authorization has been revoked. Contact the care recipient.'
                      : 'Care team cannot be modified until the CaseFile is unlocked.'}
                  </p>
                </div>
              </div>
            )}

            {/* Messages */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" role="alert">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-red-800 flex-1">{error}</p>
                <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2" role="status" aria-live="polite">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-green-800 flex-1">{successMessage}</p>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="text-green-600 hover:text-green-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Generated Code Display */}
            {generatedCode && (
              <div className="mb-4 p-4 bg-evergreen/10 border border-evergreen/30 rounded-lg">
                <p className="text-sm font-medium text-evergreen mb-2">Invitation Code</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-4 py-3 bg-white rounded-lg text-2xl font-mono tracking-wider text-center text-ink border">
                    {generatedCode}
                  </code>
                  <button
                    onClick={handleCopyCode}
                    className="btn-secondary flex items-center gap-2"
                    title="Copy code"
                  >
                    {codeCopied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-muted mt-2">
                  Share this code with the invitee. They can enter it during sign-up or in their
                  settings to join this care team.
                </p>
                <button
                  onClick={() => {
                    setGeneratedCode(null);
                    resetAddForm();
                    setShowAddForm(false);
                  }}
                  className="text-sm text-evergreen hover:underline mt-3"
                >
                  Invite someone else
                </button>
              </div>
            )}

            {/* Members Tab */}
            {activeTab === 'members' && !generatedCode && (
              <>
                {/* Current Care Team */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-ink mb-3">
                    Current Care Team ({careTeam.length} member{careTeam.length !== 1 ? 's' : ''})
                  </h3>

                  {careTeam.length > 0 ? (
                    <div className="space-y-2">
                      {careTeam.map((member) => {
                        const isOwnerMember = member.userId === ownerId;
                        return (
                          <div
                            key={member.userId}
                            className="flex items-center justify-between p-3 bg-soft-neutral/50 rounded-lg"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ink truncate">
                                {member.name || member.email || member.userId}
                                {isOwnerMember && (
                                  <span className="text-xs text-muted ml-2">(Owner)</span>
                                )}
                              </p>
                              <p className="text-xs text-muted">
                                {formatRelationship(member.relationship)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                              <span className={getRoleBadgeClass(member.role || member.permissions)}>
                                {formatRole(member)}
                              </span>
                              {!isOwnerMember && !isLocked && (
                                <button
                                  onClick={() =>
                                    handleRemoveMember(member.userId, member.name || member.email)
                                  }
                                  disabled={isRemoving === member.userId}
                                  className="btn-ghost p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  aria-label="Remove from care team"
                                >
                                  {isRemoving === member.userId ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 bg-soft-neutral/30 rounded-lg text-center">
                      <p className="text-muted text-sm">
                        No care team members yet. Invite someone to share access.
                      </p>
                    </div>
                  )}
                </div>

                {/* Add Member Form */}
                {showAddForm ? (
                  <div className="border border-soft-neutral rounded-lg p-4">
                    <h3 className="text-sm font-medium text-ink mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Send Invitation
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="ctm-member-email" className="label">Email Address *</label>
                        <input
                          id="ctm-member-email"
                          type="email"
                          value={newMemberEmail}
                          onChange={(e) => setNewMemberEmail(e.target.value)}
                          placeholder="Enter email address"
                          className="input"
                          disabled={isAdding}
                        />
                        <p className="text-xs text-muted mt-1">
                          An invitation code will be generated for this person
                        </p>
                      </div>

                      <div>
                        <label htmlFor="ctm-member-name" className="label">Name (optional)</label>
                        <input
                          id="ctm-member-name"
                          type="text"
                          value={newMemberName}
                          onChange={(e) => setNewMemberName(e.target.value)}
                          placeholder="Enter name for reference"
                          className="input"
                          disabled={isAdding}
                        />
                      </div>

                      <div>
                        <label htmlFor="ctm-member-relationship" className="label">Relationship to {careRecipientName} *</label>
                        <select
                          id="ctm-member-relationship"
                          value={newMemberRelationship}
                          onChange={(e) => setNewMemberRelationship(e.target.value as Relationship)}
                          className="input"
                          disabled={isAdding}
                        >
                          {Object.entries(RELATIONSHIP_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="label flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Access Level *
                        </label>
                        <div className="space-y-2">
                          {(
                            Object.entries(ROLE_INFO) as [Role, { label: string; description: string }][]
                          ).map(([value, info]) => (
                            <label
                              key={value}
                              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                newMemberRole === value
                                  ? 'border-evergreen bg-evergreen/5'
                                  : 'border-soft-neutral hover:border-muted'
                              }`}
                            >
                              <input
                                type="radio"
                                name="role"
                                value={value}
                                checked={newMemberRole === value}
                                onChange={() => setNewMemberRole(value)}
                                className="mt-0.5"
                                disabled={isAdding}
                              />
                              <div>
                                <p className="text-sm font-medium text-ink">{info.label}</p>
                                <p className="text-xs text-muted">{info.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                        <button
                          onClick={() => {
                            setShowAddForm(false);
                            resetAddForm();
                            setError(null);
                          }}
                          className="btn-ghost"
                          disabled={isAdding}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddMember}
                          className="btn-primary flex items-center gap-2"
                          disabled={isAdding || !newMemberEmail.trim()}
                        >
                          {isAdding ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4" />
                              Create Invitation
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddForm(true)}
                    disabled={isLocked}
                    className={`w-full p-4 border-2 border-dashed rounded-lg flex items-center justify-center gap-2 transition-colors ${
                      isLocked
                        ? 'border-soft-neutral/50 text-muted/50 cursor-not-allowed'
                        : 'border-soft-neutral text-muted hover:border-evergreen hover:text-evergreen'
                    }`}
                  >
                    <UserPlus className="w-5 h-5" />
                    {isLocked ? 'Care Team Locked' : 'Invite Care Team Member'}
                  </button>
                )}
              </>
            )}

            {/* Invitations Tab */}
            {activeTab === 'invitations' && !generatedCode && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-ink">Pending Invitations</h3>
                  <button
                    onClick={loadInvitations}
                    disabled={loadingInvitations}
                    className="btn-ghost p-1.5"
                    aria-label="Refresh invitations"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${loadingInvitations ? 'animate-spin' : ''}`}
                    />
                  </button>
                </div>

                {loadingInvitations ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted" />
                    <p className="text-sm text-muted mt-2">Loading invitations...</p>
                  </div>
                ) : invitations.length > 0 ? (
                  <div className="space-y-2">
                    {invitations.map((invite) => {
                      const statusBadge = getStatusBadge(invite.status);
                      const expiresAt = new Date(invite.expiresAt);
                      const isExpiringSoon =
                        invite.status === 'pending' &&
                        expiresAt.getTime() - Date.now() < 24 * 60 * 60 * 1000;

                      return (
                        <div
                          key={invite.code}
                          className={`p-3 rounded-lg border ${
                            invite.status === 'pending'
                              ? 'bg-white border-soft-neutral'
                              : 'bg-soft-neutral/30 border-soft-neutral/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-ink truncate">
                                  {invite.inviteeName || invite.inviteeEmail}
                                </p>
                                <span className={statusBadge.class}>{statusBadge.label}</span>
                              </div>
                              <p className="text-xs text-muted mt-0.5">
                                {formatRelationship(invite.relationship)} •{' '}
                                {formatRole(invite)}
                              </p>
                              {invite.status === 'pending' && (
                                <div className="flex items-center gap-2 mt-2">
                                  <code className="text-xs font-mono bg-soft-neutral px-2 py-1 rounded">
                                    {invite.code}
                                  </code>
                                  {isExpiringSoon && (
                                    <span className="text-xs text-amber-600 flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      Expires soon
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {invite.status === 'pending' && !isLocked && (
                              <button
                                onClick={() => handleRevokeInvitation(invite.code)}
                                disabled={isRevoking === invite.code}
                                className="btn-ghost p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                                aria-label="Revoke invitation"
                              >
                                {isRevoking === invite.code ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 bg-soft-neutral/30 rounded-lg text-center">
                    <Mail className="w-8 h-8 text-muted mx-auto mb-2" />
                    <p className="text-muted text-sm">No invitations yet</p>
                    <button
                      onClick={() => {
                        setActiveTab('members');
                        setShowAddForm(true);
                      }}
                      disabled={isLocked}
                      className="text-sm text-evergreen hover:underline mt-2"
                    >
                      Send an invitation
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Info box */}
            {!generatedCode && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-800 mb-2">
                  About Care Team Access
                </h4>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>
                    • <strong>Admin:</strong> Can edit everything and manage team members
                  </li>
                  <li>
                    • <strong>Editor:</strong> Can add/edit documents and care logs
                  </li>
                  <li>
                    • <strong>Viewer:</strong> Can only view information (read-only)
                  </li>
                  <li>
                    • <strong>Contact:</strong> No system access - stored for reference only
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-4 mt-4 border-t border-soft-neutral">
            <ModalCloseButton className="btn-secondary w-full sm:w-auto">
              Done
            </ModalCloseButton>
          </div>
    </Modal>
  );
}

export default CareTeamModal;
